import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";

function WasteForm() {
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [responsiblePerson, setResponsiblePerson] = useState(null);

  const [ingredientOptions, setIngredientOptions] = useState([]);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [wasteItems, setWasteItems] = useState([]);

  const [foodMenuOptions, setFoodMenuOptions] = useState([]);
  const [selectedFoodMenu, setSelectedFoodMenu] = useState(null);
  const [foodWasteItems, setFoodWasteItems] = useState([]);

  const [totalLoss, setTotalLoss] = useState(0);
  const [note, setNote] = useState("");
  const [reference_no, setReferenceNo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const navigate = useNavigate();
  const { id } = useParams(); // waste ID for edit
  const isEdit = Boolean(id); // true if edit mode

  /* ----------------------------- Fetch Food Menus ----------------------------- */
  const fetchItems = useCallback(() => {
    axios
      .get(`${apiKey}/api/rms/admin/foodMenus`, {
        headers: { Authorization: `Bearer ${user?.token}` },
        params: { outletId: user?.user.outlet_id, isDeal: "no" },
      })
      .then((res) => {
        const formattedFoodMenus = res.data.foodMenus.map((menu) => ({
          value: menu.id,
          label: `${menu.name} (${menu.name_arabic})`,
          price: menu.sale_price || 0,
          unit_name: menu.code || "Unit",
        }));
        setFoodMenuOptions(formattedFoodMenus);
      })
      .catch(() => toast.error("Food Menu fetch error"));
  }, [user?.token, user?.user.outlet_id]);

  /* ----------------------------- Fetch Initial Data ----------------------------- */
  useEffect(() => {
    // Responsible person
    axios
      .get(`${apiKey}/api/rms/admin/user`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      })
      .then((res) => {
        const formatted = res.data.map((u) => ({
          value: u.id,
          label: `${u.name} (${u.designation})`,
        }));
        setResponsibleOptions(formatted);
      })
      .catch(() => toast.error("User fetch error"));

    // Ingredients
    axios
      .get(`${apiKey}/api/get-ingredient`, {
        headers: { Authorization: `Bearer ${user?.token}` },
        params: { outletId: user?.user.outlet_id },
      })
      .then((res) => {
        const formattedIngredients = res.data.map((ing) => ({
          value: ing.id,
          label: `${ing.name} (${ing.code})`,
          price: ing.price,
          unit_name: ing.unit_name,
        }));
        setIngredientOptions(formattedIngredients);
      })
      .catch(() => toast.error("Ingredient fetch error"));

    fetchItems();
  }, [user?.token, fetchItems, user?.user.outlet_id]);

  /* ----------------------------- Fetch Waste Data for Edit ----------------------------- */
  useEffect(() => {
    if (!isEdit) return;
    const fetchWasteData = async () => {
      try {
        const res = await axios.get(`${apiKey}/api/rms/admin/waste/${id}`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        const data = res.data.waste;

        setReferenceNo(data.reference_no);
        setDate(data.date);
        setNote(data.note || "");

        // Wait until responsibleOptions are fetched
        const interval = setInterval(() => {
          if (responsibleOptions.length > 0) {
            const responsible = responsibleOptions.find(
              (opt) => opt.value === data.responsible_person_id
            );
            if (responsible) setResponsiblePerson(responsible);
            clearInterval(interval);
          }
        }, 100);

        const ingredientItems = data.items
          .filter((i) => i.item_type === "ingredient")
          .map((i) => ({
            id: Date.now() + Math.random(),
            ingredient: {
              value: i.item_id,
              label: i.item_name,
              price: parseFloat(i.price),
            },
            quantity: i.quantity.toString(),
            lossAmount: parseFloat(i.loss_amount),
            unit_name: i.unit_name,
          }));

        const foodItems = data.items
          .filter((i) => i.item_type === "food_menu")
          .map((i) => ({
            id: Date.now() + Math.random(),
            foodMenu: {
              value: i.item_id,
              label: i.item_name,
              price: parseFloat(i.price),
            },
            quantity: i.quantity.toString(),
            lossAmount: parseFloat(i.loss_amount),
            unit_name: i.unit_name,
          }));

        setWasteItems(ingredientItems);
        setFoodWasteItems(foodItems);
        updateTotalLoss([...ingredientItems, ...foodItems]);
      } catch (err) {
        toast.error("Failed to fetch waste data");
        console.log(err);
      }
    };
    fetchWasteData();
  }, [id, isEdit, user?.token, responsibleOptions]);

  /* ----------------------------- Add / Select Items ----------------------------- */
  const handleIngredientSelect = (selected) => {
    if (!selected) return;
    const exists = wasteItems.findIndex((i) => i.ingredient.value === selected.value);
    let updatedItems;
    if (exists !== -1) {
      updatedItems = wasteItems.map((item, idx) => {
        if (idx === exists) {
          const qty = parseFloat(item.quantity) + 1;
          return { ...item, quantity: qty.toString(), lossAmount: qty * item.ingredient.price };
        }
        return item;
      });
    } else {
      updatedItems = [
        ...wasteItems,
        {
          id: Date.now(),
          ingredient: selected,
          quantity: "1",
          lossAmount: selected.price,
          unit_name: selected.unit_name,
        },
      ];
    }
    setWasteItems(updatedItems);
    updateTotalLoss([...updatedItems, ...foodWasteItems]);
    setSelectedIngredient(null);
  };

  const handleFoodMenuSelect = (selected) => {
    if (!selected) return;
    const exists = foodWasteItems.findIndex((i) => i.foodMenu.value === selected.value);
    let updatedItems;
    if (exists !== -1) {
      updatedItems = foodWasteItems.map((item, idx) => {
        if (idx === exists) {
          const qty = parseFloat(item.quantity) + 1;
          return { ...item, quantity: qty.toString(), lossAmount: qty * item.foodMenu.price };
        }
        return item;
      });
    } else {
      updatedItems = [
        ...foodWasteItems,
        {
          id: Date.now() + Math.random(),
          foodMenu: selected,
          quantity: "1",
          lossAmount: selected.price,
          unit_name: selected.unit_name,
        },
      ];
    }
    setFoodWasteItems(updatedItems);
    updateTotalLoss([...wasteItems, ...updatedItems]);
    setSelectedFoodMenu(null);
  };

  /* ----------------------------- Quantity & Delete ----------------------------- */
  const updateQuantity = (id, qty, isIngredient = true) => {
    qty = Math.max(1, parseFloat(qty) || 1);
    const items = isIngredient ? wasteItems : foodWasteItems;
    const setter = isIngredient ? setWasteItems : setFoodWasteItems;
    const updated = items.map((item) => {
      if (item.id === id) {
        const price = isIngredient ? item.ingredient.price : item.foodMenu.price;
        return { ...item, quantity: qty.toString(), lossAmount: qty * price };
      }
      return item;
    });
    setter(updated);
    updateTotalLoss([...(isIngredient ? updated : wasteItems), ...(isIngredient ? foodWasteItems : updated)]);
  };

  const deleteItem = (id, isIngredient = true) => {
    const setter = isIngredient ? setWasteItems : setFoodWasteItems;
    const updated = (isIngredient ? wasteItems : foodWasteItems).filter((i) => i.id !== id);
    setter(updated);
    updateTotalLoss([...(isIngredient ? updated : wasteItems), ...(isIngredient ? foodWasteItems : updated)]);
  };

  const updateTotalLoss = (items) => {
    const total = items.reduce((sum, i) => sum + i.lossAmount, 0);
    setTotalLoss(total);
  };

  /* ----------------------------- Submit / Update ----------------------------- */
  const handleSubmit = async () => {
    if (!responsiblePerson) return toast.warning("Please select responsible person");
    if (wasteItems.length === 0 && foodWasteItems.length === 0)
      return toast.warning("Please add at least one waste item");

    const payload = {
      reference_no: reference_no || "0001",
      date,
      responsible_person_id: responsiblePerson.value,
      outlet_id: user?.user.outlet_id,
      note,
      items: [
        ...wasteItems.map((i) => ({
          item_type: "ingredient",
          item_id: i.ingredient.value,
          quantity: parseFloat(i.quantity),
          unit_name: i.unit_name,
          price: i.ingredient.price,
          loss_amount: i.lossAmount,
        })),
        ...foodWasteItems.map((i) => ({
          item_type: "food_menu",
          item_id: i.foodMenu.value,
          quantity: parseFloat(i.quantity),
          unit_name: i.unit_name,
          price: i.foodMenu.price,
          loss_amount: i.lossAmount,
        })),
      ],
    };

    try {
      if (isEdit) {
        await axios.put(`${apiKey}/api/rms/admin/waste/${id}`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        toast.success("Waste Updated Successfully!");
      } else {
        await axios.post(`${apiKey}/api/rms/admin/waste`, payload, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        toast.success("Waste Added Successfully!");
      }
      navigate(-1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Submit error");
      console.log(err);
    }
  };

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-[#7B20C7] text-2xl">{isEdit ? "Edit Waste" : "Add Waste"}</h1>
        </div>
        <div className="w-full">
          <form
            action=""
            className="w-full"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Reference No
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="0001"
                    value={reference_no}
                    onChange={(e) => setReferenceNo(e.target.value)}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Date
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Responsible Person
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    options={responsibleOptions}
                    value={responsiblePerson}
                    onChange={(e) => setResponsiblePerson(e)}
                    placeholder="Select User"
                    className="px-2 py-2 text-sm shadow rounded bg-white"
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full pt-3">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Ingredients
                </span>
                <span className="text-xs mx-3">
                  (Only purchased Ingredients are listed)
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    options={ingredientOptions}
                    value={selectedIngredient}
                    onChange={handleIngredientSelect}
                    placeholder="Select"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/3">
                <span className="text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Food Menu
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    options={foodMenuOptions}
                    value={selectedFoodMenu}
                    onChange={handleFoodMenuSelect}
                    placeholder="Select"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>
            </div>

            {/* Dynamic Table for Waste Items */}
            {wasteItems.length > 0 && (
              <div className="w-full pt-3 pb-3 overflow-x-auto">
                <table className="w-full table-auto border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left font-semibold py-2 px-2">SN</th>
                      <th className="text-left font-semibold py-2 px-2">Ingredient (Code)</th>
                      <th className="text-left font-semibold py-2 px-2">Quantity/Unit</th>
                      <th className="text-left font-semibold py-2 px-2">Loss Amount</th>
                      <th className="text-left font-semibold py-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wasteItems.map((item, index) => (
                      <tr key={item.id} className="border-b">
                        <td className="py-2 px-2 text-sm">{index + 1}</td>
                        <td className="py-2 px-2 text-sm capitalize">{item.ingredient.label}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, e.target.value)}
                              className="px-2 py-1 text-sm shadow rounded w-20"
                            />
                            <span className="ml-2 text-sm">{item.unit_name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={item.lossAmount.toFixed(2)}
                              readOnly
                              className="px-2 py-1 text-sm shadow rounded w-20 bg-gray-100"
                            />
                            <span className="ml-2 text-sm">S.R</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <button
                            type="button"
                            onClick={() => deleteItem(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width={14} height={14}>
                              <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

           {/* Dynamic Table for Food Menu Waste Items */}
            {foodWasteItems.length > 0 && (
              <div className="w-full pt-3 pb-3 overflow-x-auto">
                <h2 className="text-lg bg-d-cat-btn text-[#fff] font-semibold mb-2">Food Menu Waste Items</h2>
                <table className="w-full table-auto border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left font-semibold py-2 px-2">SN</th>
                      <th className="text-left font-semibold py-2 px-2">Food Menu</th>
                      <th className="text-left font-semibold py-2 px-2">Quantity/Code</th>
                      <th className="text-left font-semibold py-2 px-2">Loss Amount</th>
                      <th className="text-left font-semibold py-2 px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {foodWasteItems.map((item, index) => (
                      <tr key={item.id} className="border-b">
                        <td className="py-2 px-2 text-sm">{index + 1}</td>
                        <td className="py-2 px-2 text-sm capitalize">{item.foodMenu.label}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, e.target.value, false)}
                              className="px-2 py-1 text-sm shadow rounded w-20"
                              min="1"
                            />
                            <span className="ml-2 text-sm">{item.unit_name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="flex items-center">
                            <input
                              type="text"
                              value={item.lossAmount.toFixed(2)}
                              readOnly
                              className="px-2 py-1 text-sm shadow rounded w-20 bg-gray-100"
                            />
                            <span className="ml-2 text-sm">S.R</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <button
                            type="button"
                            onClick={() => deleteItem(item.id, false)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width={14} height={14}>
                              <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-col lg:flex-row gap-x-8 gap-y-2 w-full pt-3">
              <label htmlFor="" className="w-full lg:w-1/3">
                <span className=" text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Total Loss
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    value={totalLoss.toFixed(2)}
                    readOnly
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full lg:w-1/3">
                <span className=" text-[#7B20C7] text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2">
                  Note
                </span>
                <div className="mb-2 pt-0">
                  <textarea
                    placeholder="ENTER...."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="px-2 py-5 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                  />
                </div>
              </label>
            </div>

            <span className="w-full bg-[#7B20C7] h-1 my-2 inline-block"></span>
          <div className="flex gap-2">
            <button
              className="bg-[#7B20C7] text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none w-fit"
              type="button"
              onClick={handleSubmit}
            >
              {isEdit ? "Update" : "Submit"}
            </button>
            <button
              className="bg-[#7B20C7] text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none w-fit"
              type="button"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default WasteForm;
